import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import Nav from "./components/Nav";
import NavRight from "./components/NavRight";
import {
  featuredJobs,
  getSuggestedGroups,
  latestJobPosts,
  publicGroups,
} from "./data";

const HomePage = () => {
  // Get suggested groups using helper function
  const suggestedGroups = getSuggestedGroups(publicGroups, 3);

  const handleJoinGroup = (groupId: number) => {
    // Handle join group logic
    console.log(`Joining group ${groupId}`);
  };

  return (
    <div className="pt-[100px] min-h-screen">
      <Nav />
      <div className="lg:pl-[280px] xl:pr-[350px] px-4 lg:px-0">
        {/* Hero Section with Search */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 mx-0 lg:mx-4 rounded-xl  overflow-hidden relative">
          {/* Background decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full -translate-y-32 translate-x-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-green-200/20 to-blue-200/20 rounded-full translate-y-24 -translate-x-24"></div>

          <div className="relative px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-7xl mx-auto">
              {/* Left Content */}
              <div className="text-center lg:text-left order-2 lg:order-1">
                <div className="mb-6">
                  <Badge
                    variant="secondary"
                    className="mb-4 px-4 lg:px-6 py-2 bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    🌟 Kết nối qua cộng đồng chuyên nghiệp
                  </Badge>
                </div>

                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Tìm việc qua{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                    cộng đồng
                  </span>
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                    ngành nghề
                  </span>
                </h1>

                <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0">
                  Tham gia các nhóm chuyên nghiệp, kết nối với đồng nghiệp và
                  khám phá cơ hội việc làm phù hợp với bạn
                </p>

                {/* Search Bar */}
                <div className="max-w-lg mx-auto lg:mx-0 mb-8">
                  <div className="relative group">
                    <Input
                      type="text"
                      placeholder="Tìm kiếm nhóm, việc làm, kỹ năng..."
                      className="w-full pl-12 lg:pl-14 pr-24 lg:pr-32 py-3 lg:py-4 text-base lg:text-lg border-2 border-gray-50 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                    />
                    <div className="absolute left-4 lg:left-5 top-1/2 transform -translate-y-1/2">
                      <svg
                        className="w-5 lg:w-6 h-5 lg:h-6 text-gray-400 group-focus-within:text-blue-500 transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <Button className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 lg:px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded text-sm lg:text-base">
                      <span className="hidden sm:inline">Tìm kiếm</span>
                      <span className="sm:hidden">Tìm</span>
                    </Button>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 lg:gap-6 pt-6 lg:pt-8 border-t border-gray-50">
                  <div className="text-center">
                    <div className="text-2xl lg:text-3xl font-bold text-blue-600 mb-1">
                      150+
                    </div>
                    <div className="text-xs lg:text-sm text-gray-500">
                      Nhóm chuyên nghiệp
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl lg:text-3xl font-bold text-green-600 mb-1">
                      2.5K+
                    </div>
                    <div className="text-xs lg:text-sm text-gray-500">
                      Việc làm mới
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl lg:text-3xl font-bold text-purple-600 mb-1">
                      50K+
                    </div>
                    <div className="text-xs lg:text-sm text-gray-500">
                      Thành viên tích cực
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Visual */}
              <div className="relative order-1 lg:order-2 mb-8 lg:mb-0">
                <div className="relative z-10">
                  {/* Main illustration container */}
                  <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl lg:rounded-3xl p-6 lg:p-8 shadow-2xl">
                    {/* Floating cards representing different groups */}
                    <div className="space-y-3 lg:space-y-4">
                      {/* Tech Group Card */}
                      <div className="bg-white rounded-xl lg:rounded-2xl p-3 lg:p-4 shadow-lg transform rotate-2 hover:rotate-0 transition-transform duration-300">
                        <div className="flex items-center space-x-2 lg:space-x-3">
                          <div className="w-10 lg:w-12 h-10 lg:h-12 bg-blue-100 rounded-lg lg:rounded-xl flex items-center justify-center text-xl lg:text-2xl">
                            👗
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm lg:text-base">
                              Công nghiệp May mặc
                            </h3>
                            <p className="text-xs lg:text-sm text-gray-600">
                              24.3k thành viên
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Design Group Card */}
                      <div className="bg-white rounded-xl lg:rounded-2xl p-3 lg:p-4 shadow-lg transform -rotate-1 hover:rotate-0 transition-transform duration-300 ml-6 lg:ml-8">
                        <div className="flex items-center space-x-2 lg:space-x-3">
                          <div className="w-10 lg:w-12 h-10 lg:h-12 bg-purple-100 rounded-lg lg:rounded-xl flex items-center justify-center text-xl lg:text-2xl">
                            🍽️
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm lg:text-base">
                              Nhà hàng & Khách sạn
                            </h3>
                            <p className="text-xs lg:text-sm text-gray-600">
                              18.7k thành viên
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Marketing Group Card */}
                      <div className="bg-white rounded-xl lg:rounded-2xl p-3 lg:p-4 shadow-lg transform rotate-1 hover:rotate-0 transition-transform duration-300">
                        <div className="flex items-center space-x-2 lg:space-x-3">
                          <div className="w-10 lg:w-12 h-10 lg:h-12 bg-green-100 rounded-lg lg:rounded-xl flex items-center justify-center text-xl lg:text-2xl">
                            🏭
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm lg:text-base">
                              Chế biến Thực phẩm
                            </h3>
                            <p className="text-xs lg:text-sm text-gray-600">
                              15.8k thành viên
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating elements around the main illustration */}
                  <div className="absolute -top-6 lg:-top-8 -left-6 lg:-left-8 w-12 lg:w-16 h-12 lg:h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl lg:rounded-2xl shadow-lg flex items-center justify-center text-lg lg:text-2xl transform rotate-12 hover:rotate-0 transition-transform duration-300">
                    🚀
                  </div>

                  <div className="absolute -bottom-4 lg:-bottom-6 -right-4 lg:-right-6 w-16 lg:w-20 h-16 lg:h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-full shadow-lg flex items-center justify-center text-lg lg:text-2xl transform -rotate-12 hover:rotate-0 transition-transform duration-300">
                    💼
                  </div>

                  <div className="absolute top-1/4 -right-6 lg:-right-8 w-12 lg:w-14 h-12 lg:h-14 bg-gradient-to-br from-pink-400 to-purple-500 rounded-lg lg:rounded-xl shadow-lg flex items-center justify-center text-base lg:text-xl transform rotate-45 hover:rotate-0 transition-transform duration-300">
                    👥
                  </div>
                </div>

                {/* Background glow effects */}
                <div className="absolute top-10 right-10 w-32 lg:w-40 h-32 lg:h-40 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
                <div
                  className="absolute bottom-10 left-10 w-24 lg:w-32 h-24 lg:h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"
                  style={{ animationDelay: "1s" }}
                ></div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Jobs of the Day */}
        <section className="py-6 lg:py-8 px-0 lg:px-4">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
                  🔥 Việc làm nổi bật hôm nay
                </h2>
                <p className="text-sm lg:text-base text-gray-600">
                  Những cơ hội nghề nghiệp được quan tâm nhất
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="sm:self-start"
              >
                Xem tất cả
              </Button>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {featuredJobs.map((job) => (
              <Card
                key={job.id}
                className="hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-50"
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start mb-2">
                    <Badge
                      variant="outline"
                      className="text-blue-600 border-blue-200 text-xs"
                    >
                      {job.group}
                    </Badge>
                  </div>
                  <CardTitle className="text-base lg:text-lg hover:text-blue-600 transition-colors line-clamp-2">
                    {job.title}
                  </CardTitle>
                  <CardDescription className="font-medium text-gray-700 text-sm">
                    {job.company}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {job.postedTime}
                    </span>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-xs lg:text-sm"
                    >
                      Ứng tuyển
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Public Groups Section */}
        <section className="py-6 lg:py-8 px-0 lg:px-4">
          <div className="mb-6">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
              👥 Cộng đồng chuyên nghiệp
            </h2>
            <p className="text-sm lg:text-base text-gray-600">
              Tham gia các nhóm theo ngành nghề để kết nối và tìm cơ hội
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {publicGroups.map((group) => (
              <Card
                key={group.id}
                className="hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-50"
              >
                <CardContent className="p-4 lg:p-6">
                  <div className="flex items-start space-x-3 lg:space-x-4 mb-4">
                    <div className="w-10 lg:w-12 h-10 lg:h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg lg:rounded-xl flex items-center justify-center text-xl lg:text-2xl flex-shrink-0">
                      {group.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate text-sm lg:text-base">
                        {group.name}
                      </h3>
                      <p className="text-xs lg:text-sm text-gray-600 line-clamp-2">
                        {group.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs lg:text-sm text-gray-500 mb-4">
                    <span>
                      👥 {group.memberCount.toLocaleString()} thành viên
                    </span>
                    <span>💼 {group.jobCount} việc làm</span>
                  </div>

                  {group.isJoined ? (
                    <Button
                      variant="outline"
                      className="w-full text-xs lg:text-sm"
                      disabled
                    >
                      ✓ Đã tham gia
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-xs lg:text-sm"
                      onClick={() => handleJoinGroup(group.id)}
                    >
                      Tham gia nhóm
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Latest Job Posts from Joined Groups */}
        <section className="py-6 lg:py-8 px-0 lg:px-4">
          <div className="mb-6">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
              📋 Việc làm mới từ nhóm đã tham gia
            </h2>
            <p className="text-sm lg:text-base text-gray-600">
              Cập nhật mới nhất từ các cộng đồng bạn đang theo dõi
            </p>
          </div>
          <div className="space-y-4">
            {latestJobPosts.map((job) => (
              <Card
                key={job.id}
                className="hover:shadow-md transition-shadow border border-gray-50"
              >
                <CardContent className="p-4 lg:p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mb-2">
                        <Badge
                          variant="outline"
                          className="text-blue-600 border-blue-200 text-xs w-fit"
                        >
                          {job.group}
                        </Badge>
                        <span className="text-xs lg:text-sm text-gray-500">
                          • {job.postedTime}
                        </span>
                      </div>
                      <h3 className="font-semibold text-base lg:text-lg text-gray-900 hover:text-blue-600 cursor-pointer mb-2">
                        {job.title}
                      </h3>
                      <p className="text-gray-700 font-medium mb-2 text-sm lg:text-base">
                        {job.company}
                      </p>
                    </div>
                    <div className="flex flex-row lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2">
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-xs lg:text-sm flex-1 lg:flex-none"
                      >
                        Ứng tuyển
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs lg:text-sm flex-1 lg:flex-none"
                      >
                        Lưu
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-6">
            <Button
              variant="outline"
              size="lg"
              className="text-sm lg:text-base"
            >
              Xem thêm việc làm
            </Button>
          </div>
        </section>

        {/* Suggested Groups */}
        <section className="py-6 lg:py-8 px-0 lg:px-4">
          <div className="mb-6">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
              ✨ Nhóm gợi ý cho bạn
            </h2>
            <p className="text-sm lg:text-base text-gray-600">
              Dựa trên hồ sơ và sở thích của bạn
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {suggestedGroups.map((group) => (
              <Card
                key={group.id}
                className="hover:shadow-lg transition-all duration-300 cursor-pointer border-2 border-dashed border-gray-50 hover:border-blue-300"
              >
                <CardContent className="p-4 lg:p-6 text-center">
                  <div className="w-12 lg:w-16 h-12 lg:h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center text-2xl lg:text-3xl mx-auto mb-4">
                    {group.avatar}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm lg:text-base">
                    {group.name}
                  </h3>
                  <p className="text-xs lg:text-sm text-gray-600 mb-4 line-clamp-2">
                    {group.description}
                  </p>
                  <div className="flex justify-center space-x-4 text-xs text-gray-500 mb-4">
                    <span>{group.memberCount.toLocaleString()} thành viên</span>
                    <span>{group.jobCount} việc làm</span>
                  </div>
                  <Button
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-xs lg:text-sm"
                    onClick={() => handleJoinGroup(group.id)}
                  >
                    Tham gia ngay
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>
      <NavRight />
    </div>
  );
};

export default HomePage;
